
// Sites API service for fetching data from Supabase
import { supabase } from "@/integrations/supabase/client";
import { Site } from "./types";
import { mapDbToSite } from "./siteUtils";
import { getBelleRecyclageSite, getBrianeJeanSite } from "./hardcodedSites";

// Re-export types from types.ts for backward compatibility
export type { OpeningHours, OpeningHoursSchedule, Site } from "./types";

// Get all sites from Supabase
export const fetchSites = async (): Promise<Site[]> => {
  try {
    const { data, error } = await supabase
      .from('sites')
      .select('*');
    
    if (error) {
      console.error('Error fetching sites:', error);
      throw error;
    }
    
    // Map database sites
    const dbSites = data.map(mapDbToSite);
    
    // Add hardcoded sites
    const hardcodedSites = [
      getBelleRecyclageSite(),
      getBrianeJeanSite()
    ];
    
    // Combine all sites
    return [...dbSites, ...hardcodedSites];
  } catch (error) {
    console.error('Failed to fetch sites:', error);
    throw error;
  }
};

// Get a single site by ID or slug
export const getSiteById = async (id: string): Promise<Site | undefined> => {
  // Special cases for hardcoded sites - handle both UUID and slug
  if (id === "ab3cc5c8-aab4-4284-a282-37384f3c6e45" || id === "belle-recyclage") {
    return getBelleRecyclageSite();
  }
  
  if (id === "bf775ebc-24ce-45b2-ac61-953a6f34652e" || id === "briane-jean-recuperation") {
    return getBrianeJeanSite();
  }

  // Handle slug-based routing for other sites
  const slugToUuidMap: Record<string, string> = {
    "profit-recyclage": "5241f80d-9340-4513-b210-5bc29e1cb54e",
    "lescout-recyclage": "ac8d4f12-1234-5678-9abc-def012345678",
    "fabrude-recyclage": "fd8e5c23-2345-6789-abcd-ef0123456789",
    "casse-auto-bassin": "ea7d4b34-3456-789a-bcde-f01234567890",
    "rm-recyclage": "db6c3a45-4567-89ab-cdef-012345678901",
    "bennes-profit": "cb5b2956-5678-9abc-def0-123456789012",
    "carmausine-recuperation": "f151588c-e129-4bfc-be02-69240ed00223"
  };

  // Convert slug to UUID if needed
  const actualId = slugToUuidMap[id] || id;

  try {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .eq('id', actualId)
      .single();
    
    if (error) {
      // If not found, don't throw an error
      if (error.code === 'PGRST116') {
        return undefined;
      }
      console.error('Error fetching site by ID:', error);
      throw error;
    }
    
    return mapDbToSite(data);
  } catch (error) {
    console.error(`Failed to fetch site with ID ${id}:`, error);
    throw error;
  }
};

// Get sites by service
export const getSitesByService = async (service: string): Promise<Site[]> => {
  try {
    const { data, error } = await supabase
      .from('sites')
      .select('*')
      .contains('services', [service]);
    
    if (error) {
      console.error(`Error fetching sites by service ${service}:`, error);
      throw error;
    }
    
    return data.map(mapDbToSite);
  } catch (error) {
    console.error(`Failed to fetch sites with service ${service}:`, error);
    throw error;
  }
};
